import { resample } from "wave-resampler"

// Reflection padding on each end before resampling, to avoid boundary
// artifacts from the FIR filter convolving with zero-padding past the edge
// of the signal. Padded region is trimmed off the output at the corresponding
// resampled length. ~40 samples covers the default FIR kernel width; clamped
// to `n - 1` for short signals so the mirror doesn't run off the far edge.
const PAD_SAMPLES = 40

// Change the sample rate of a 1D signal from `sourceHz` to `targetHz`.
// Thin wrapper around wave-resampler that returns a Float64Array. Used to
// upsample median-beat waveforms (150, 300, or 500 Hz depending on lead
// configuration) to a common target rate for downstream analysis.
//
// Returns the input unchanged if source and target rates match.
export const resampleSignal = (samples, sourceHz, targetHz) => {
    if (!samples || samples.length === 0) return new Float64Array(0)
    if (sourceHz === targetHz) return Float64Array.from(samples)

    const n = samples.length
    const pad = Math.max(0, Math.min(PAD_SAMPLES, n - 1))

    // Reflection-pad both ends. Symmetric reflection: excludes the boundary
    // sample itself so we don't duplicate it.
    const padded = new Float64Array(n + 2 * pad)
    for (let i = 0; i < pad; i++) padded[i] = samples[pad - i]
    for (let i = 0; i < n; i++) padded[pad + i] = samples[i]
    for (let i = 0; i < pad; i++) padded[pad + n + i] = samples[n - 2 - i]

    const resampled = resample(padded, sourceHz, targetHz, { method: "sinc", LPF: true, LPFType: "FIR" })

    // Trim the mirrored region from the output. Uses the same source→target
    // ratio the resampler applied to the input length.
    const outPad = Math.round(pad * targetHz / sourceHz)
    return resampled.slice(outPad, resampled.length - outPad)
}
